"use strict";
/**
 * Data Source Service
 * Handles switching between Kalshi and Polymarket data sources
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CUSTOM_KALSHI_SUFFIX_VALUE = exports.CUSTOM_KALSHI_PREFIX_VALUE = exports.CUSTOM_KALSHI_LEAGUE_VALUE = void 0;
exports.populateKalshiPrefixes = populateKalshiPrefixes;
exports.populateKalshiSuffixes = populateKalshiSuffixes;
exports.populateKalshiLeagues = populateKalshiLeagues;
exports.populateFlatLeagueSelector = populateFlatLeagueSelector;
exports.switchDataSource = switchDataSource;
const state_1 = require("../state");
const markets_1 = require("../markets");
const registry_1 = require("../venues/registry");
const capabilities_1 = require("../venues/capabilities");
const operator_error_1 = require("../api/operator-error");
const venues_1 = require("../../shared/venues");
const const_1 = require("../const");
const ui_1 = require("../ui");
const SPORT_PRIORITY = {
    nba: 1,
    nhl: 2,
    mlb: 3,
    nfl: 4,
    cbb: 5,
    cwbb: 6,
    mls: 7,
    mma: 8,
    wnba: 9,
};
/**
 * Order leagues by operator priority, then alphabetically.
 *
 * Keyed on the lowercased sort key: Polymarket's sport names arrive lowercase
 * and PM-US's do not, and the two copies this replaced differed on exactly that
 * (one lowercased, one did not). Lowercasing is a no-op for the former.
 */
function sortBySportPriority(leagues) {
    const rank = (league) => SPORT_PRIORITY[(league.sortKey ?? league.label).toLowerCase()] ?? 99;
    return [...leagues].sort((a, b) => {
        const diff = rank(a) - rank(b);
        return diff !== 0 ? diff : a.label.localeCompare(b.label);
    });
}
exports.CUSTOM_KALSHI_LEAGUE_VALUE = "__custom__";
/** Sentinel option value for the "➕ Custom…" entry in the prefix selector. */
exports.CUSTOM_KALSHI_PREFIX_VALUE = "__custom_prefix__";
/** Sentinel option value for the "➕ Custom…" entry in the suffix selector. */
exports.CUSTOM_KALSHI_SUFFIX_VALUE = "__custom_suffix__";
/**
 * Append persisted custom entries (that aren't already built-in) followed by a
 * "➕ Custom…" sentinel option to a Kalshi prefix/suffix selector.
 */
function appendCustomEntries(selector, customValues, builtInValues, sentinelValue) {
    customValues.forEach((value) => {
        if (!builtInValues.has(value)) {
            const option = document.createElement("option");
            option.value = value;
            option.textContent = `Custom: ${value}`;
            option.dataset.customValue = "true";
            selector.appendChild(option);
        }
    });
    const customOption = document.createElement("option");
    customOption.value = sentinelValue;
    customOption.textContent = "➕ Custom…";
    customOption.dataset.customEntry = "true";
    selector.appendChild(customOption);
}
/**
 * Populate the events-page league *prefix* selector (`KXNBA`, `KXMLB`, …).
 * The full series ticker is this prefix plus a market-type suffix; see
 * {@link populateKalshiSuffixes}.
 */
function populateKalshiPrefixes(selector) {
    if (!selector)
        return;
    selector.innerHTML = "";
    selector.disabled = false;
    const builtInValues = new Set(const_1.KALSHI_LEAGUE_PREFIXES.map((p) => p.value));
    const_1.KALSHI_LEAGUE_PREFIXES.forEach(({ value, label }) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = label;
        selector.appendChild(option);
    });
    appendCustomEntries(selector, (0, state_1.loadCustomPrefixes)(), builtInValues, exports.CUSTOM_KALSHI_PREFIX_VALUE);
    // Restore the favourite prefix if it still exists, else default to the first.
    const favorite = (0, state_1.getKalshiSeriesFavorite)();
    const defaultPrefix = const_1.KALSHI_LEAGUE_PREFIXES[0]?.value ?? "";
    if (favorite &&
        selector.querySelector(`option[value="${CSS.escape(favorite.prefix)}"]`)) {
        selector.value = favorite.prefix;
    }
    else {
        selector.value = defaultPrefix;
    }
}
/**
 * Populate the events-page market-type *suffix* selector for a given league
 * prefix. The option set is league-specific (baseball offers F5, court sports
 * offer quarters/halves — see {@link getKalshiSuffixOptions}).
 *
 * Selection priority:
 *  - `preserveSelection` (a prefix change mid-session): keep the current suffix
 *    if it exists in the new set, so switching NBA→NFL keeps e.g. `SPREAD`.
 *  - otherwise (initial load / venue switch): restore the favourite suffix.
 *  - falling back to {@link DEFAULT_KALSHI_SUFFIX} (`GAME`), present in every set.
 */
function populateKalshiSuffixes(selector, prefix, options = {}) {
    if (!selector)
        return;
    const previous = selector.value;
    selector.innerHTML = "";
    selector.disabled = false;
    const suffixOptions = (0, const_1.getKalshiSuffixOptions)(prefix);
    const builtInValues = new Set(suffixOptions.map((s) => s.value));
    suffixOptions.forEach(({ value, label }) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = label;
        selector.appendChild(option);
    });
    appendCustomEntries(selector, (0, state_1.loadCustomSuffixes)(), builtInValues, exports.CUSTOM_KALSHI_SUFFIX_VALUE);
    const hasOption = (value) => !!value &&
        !!selector.querySelector(`option[value="${CSS.escape(value)}"]`);
    const favorite = (0, state_1.getKalshiSeriesFavorite)();
    let desired = const_1.DEFAULT_KALSHI_SUFFIX;
    if (options.preserveSelection && hasOption(previous)) {
        desired = previous;
    }
    else if (!options.preserveSelection && favorite && hasOption(favorite.suffix)) {
        desired = favorite.suffix;
    }
    selector.value = hasOption(desired) ? desired : const_1.DEFAULT_KALSHI_SUFFIX;
}
/**
 * Populate category selector with Kalshi leagues
 */
function populateKalshiLeagues(selector) {
    if (!selector)
        return;
    selector.innerHTML = "";
    const leagues = [
        { value: const_1.LEAGUES.NBA, label: "🏀 NBA" },
        { value: const_1.LEAGUES.NFL, label: "🏈 NFL" },
        { value: const_1.LEAGUES.MLB, label: "⚾ MLB" },
        { value: const_1.LEAGUES.NHL, label: "🏒 NHL" },
        { value: const_1.LEAGUES.CBB, label: "🏀 NCAA M" },
        { value: const_1.LEAGUES.WCBB, label: "🏀 NCAA W" },
        { value: const_1.LEAGUES.CFB, label: "🏈 NCAA F" },
        { value: const_1.LEAGUES.MLS, label: "⚽ MLS" },
        { value: const_1.LEAGUES.CBSBL, label: "⚾ NCAA Baseball" },
        { value: const_1.LEAGUES.CHKY, label: "🏒 NCAA Hockey" },
        { value: const_1.LEAGUES.WNBA, label: "🏀 WNBA" },
    ];
    const builtInValues = new Set(leagues.map((l) => l.value));
    leagues.forEach(({ value, label }) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = label;
        selector.appendChild(option);
    });
    // Add persisted custom series entries
    const customSeries = (0, state_1.loadCustomSeries)();
    customSeries.forEach((ticker) => {
        if (!builtInValues.has(ticker)) {
            const option = document.createElement("option");
            option.value = ticker;
            option.textContent = `Custom: ${ticker}`;
            option.dataset.customValue = "true";
            selector.appendChild(option);
        }
    });
    const customOption = document.createElement("option");
    customOption.value = exports.CUSTOM_KALSHI_LEAGUE_VALUE;
    customOption.textContent = "➕ Custom…";
    customOption.dataset.customEntry = "true";
    selector.appendChild(customOption);
    // Select favorite league if stored, otherwise default to NBA
    const favorite = (0, state_1.getFavoriteLeague)("kalshi");
    if (favorite &&
        selector.querySelector(`option[value="${CSS.escape(favorite)}"]`)) {
        selector.value = favorite;
    }
    else {
        selector.value = const_1.LEAGUES.NBA;
    }
}
/**
 * Populate a flat league selector for any venue whose `leagueSelector` is
 * `"flat-list"` (Polymarket, PM-US, Novig).
 *
 * This replaced two ~90%-identical copies; a third was about to be written for
 * Novig. The adapter supplies the rows (`fetchLeagues`) and everything DOM —
 * loading state, priority sort, favourite restore, failure message — lives
 * here, with the rest of the selector code.
 */
async function populateFlatLeagueSelector(selector, venue) {
    if (!selector)
        return;
    const fetchLeagues = (0, registry_1.getVenue)(venue).fetchLeagues;
    if (!fetchLeagues) {
        console.error(`[leagues] ${venue} has no flat league list`);
        return;
    }
    try {
        selector.innerHTML = '<option value="">Loading sports...</option>';
        selector.disabled = true;
        const leagues = sortBySportPriority(await fetchLeagues());
        // Legacy state, kept byte-identical per venue: `slug` is present for
        // PM-US and absent for Polymarket, exactly as before. Nothing reads it
        // (flagged in REVIEW_FOR_DELETION.md), so it is preserved, not extended.
        (0, state_1.updateState)({
            polymarketSports: leagues.map((league) => ({
                id: league.id ?? 0,
                sport: league.label,
                series: league.value,
                ...(league.slug !== undefined ? { slug: league.slug } : {}),
            })),
        });
        selector.innerHTML = "";
        if (leagues.length === 0) {
            const option = document.createElement("option");
            option.value = "";
            option.textContent = "No sports available";
            selector.appendChild(option);
            selector.disabled = true;
            return;
        }
        leagues.forEach((league) => {
            const option = document.createElement("option");
            option.value = league.value;
            option.textContent = league.label;
            selector.appendChild(option);
        });
        selector.disabled = false;
        const favorite = (0, state_1.getFavoriteLeague)(venue);
        if (favorite &&
            selector.querySelector(`option[value="${CSS.escape(favorite)}"]`)) {
            selector.value = favorite;
        }
        else {
            selector.selectedIndex = 0;
        }
    }
    catch (error) {
        console.error(`Failed to fetch ${(0, venues_1.venueLabel)(venue)} sports:`, error);
        // A venue that explained itself (not configured, open the Novig app…)
        // says so; "try again" would send the operator in circles.
        const message = (0, operator_error_1.operatorMessage)(error, `Failed to load ${(0, venues_1.venueLabel)(venue)} sports. Please try again.`);
        selector.innerHTML = '<option value="">Failed to load sports</option>';
        selector.disabled = true;
        (0, ui_1.showError)(message, "events-error");
    }
}
/**
 * Switch the active data source and repopulate its league selector.
 *
 * For Kalshi the `categorySelector` holds the league *prefix* and the optional
 * `suffixSelector` holds the market-type suffix; for a flat-list venue the
 * `categorySelector` holds the league and the suffix selector is unused (the
 * caller hides it).
 */
async function switchDataSource(newSource, categorySelector, suffixSelector) {
    (0, state_1.updateState)({ dataSource: newSource });
    (0, state_1.saveDataSourceToStorage)();
    (0, markets_1.clearMarketCache)();
    // Which SHAPE of league selector, not which venue — see venues/capabilities.
    if ((0, capabilities_1.usesSeriesSelector)(newSource)) {
        populateKalshiPrefixes(categorySelector);
        populateKalshiSuffixes(suffixSelector ?? null, categorySelector?.value ?? null);
    }
    else {
        await populateFlatLeagueSelector(categorySelector, newSource);
    }
}
